import type { ComponentType, SVGProps } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  FileText,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Package,
  Upload,
  Users,
} from 'lucide-react';
import { useAuth } from '@/lib/auth/useAuth';
import { useWells } from '@/features/wells/api';
import { isDataUploadsEnabled, isWellsEnabled } from '@/lib/features';
import { cn } from '@/lib/utils';
// Vite bundles the SVG as a URL. Replace the file itself
// (apps/frontend/src/assets/raptor-logo.svg) to swap the logo — no code
// change needed. Placeholder wordmark ships today; the real Raptor logo
// drops into that same path.
import raptorLogoUrl from '@/assets/raptor-logo.svg';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface NavItemSpec {
  to: string;
  label: string;
  Icon: IconComponent;
}

// Nav is built lazily so a compiled bundle with VITE_FEATURE_DATA_UPLOADS=false
// omits the Uploads entry entirely (rather than rendering-and-hiding it).
function buildCustomerNav(hasWells: boolean): NavItemSpec[] {
  const nav: NavItemSpec[] = [
    { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/issues', label: 'Issues', Icon: ListChecks },
  ];
  if (isDataUploadsEnabled()) {
    nav.push({ to: '/uploads', label: 'Uploads', Icon: Upload });
  }
  // Shown only when the customer actually has wells provisioned (chunk 4) AND
  // the Wells surface is switched on — the launch ships without it.
  if (hasWells && isWellsEnabled()) {
    nav.push({ to: '/wells', label: 'Wells', Icon: Gauge });
  }
  nav.push({ to: '/releases', label: 'Releases', Icon: Package });
  return nav;
}

const adminNav: NavItemSpec[] = [
  { to: '/admin/customers', label: 'Customers', Icon: Users },
  { to: '/admin/audit-log', label: 'Audit Log', Icon: FileText },
];

export function DashboardLayout() {
  const { user, hasRole, logout } = useAuth();
  const isAdmin = hasRole('admin');
  const displayName = user?.name ?? user?.username ?? '';
  // Gate the Wells nav item on the customer actually having wells. Cheap +
  // cached; admins (no customers row) get an empty hierarchy → no Wells item.
  const wells = useWells();
  const hasWells = (wells.data?.sites ?? []).some((s) => s.wells.length > 0);
  const customerNav = buildCustomerNav(hasWells);

  return (
    <div className="flex min-h-screen bg-muted/40">
      <aside className="flex w-64 flex-col bg-sidebar text-sidebar-foreground">
        <div className="px-6 py-5" data-testid="sidebar-logo">
          {/* The placeholder wordmark is all-white and reads well on the
              navy sidebar. If the real brand later ships a dark-on-light
              variant, swap the file at src/assets/raptor-logo.svg — the
              img inherits its size and the sidebar background handles
              contrast. */}
          <img src={raptorLogoUrl} alt="Raptor" className="h-7 w-auto" aria-label="Raptor Portal" />
        </div>
        <nav className="flex flex-col gap-1 px-3">
          {customerNav.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}

          {isAdmin && (
            <div className="flex flex-col gap-1" data-testid="admin-nav">
              <div className="my-3 border-t border-sidebar-border" />
              <div className="px-3 py-1 text-xs uppercase tracking-wide text-sidebar-muted-foreground">
                Admin
              </div>
              {adminNav.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>
          )}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b bg-background px-6 py-3">
          <div className="text-sm text-muted-foreground">
            {isAdmin ? 'Staff console' : 'Customer portal'}
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="text-right" data-testid="header-user-info">
                <div className="text-sm text-foreground" data-testid="header-display-name">
                  {displayName}
                </div>
                <div className="text-xs text-muted-foreground" data-testid="header-email">
                  {user.email}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={logout}
              data-testid="header-logout-button"
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground hover:bg-muted/60"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, label, Icon }: NavItemSpec) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'inline-flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-sidebar-active text-sidebar-foreground'
            : 'text-sidebar-muted-foreground hover:bg-sidebar-active/60 hover:text-sidebar-foreground',
        )
      }
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </NavLink>
  );
}
